import React, { useState, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Button,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  Grid,
  Divider,
} from '@mui/material';
import {
  Warning,
  Error,
  Info,
  Security,
  PlayArrow,
  ContentCopy,
  Speed,
  ExpandMore,
  GetApp,
  Visibility,
  CheckCircle,
  Schedule,
  Shield,
  Videocam,
  VideoLibrary,
} from '@mui/icons-material';
import InfractionComponent from './Infraction';
import VideoReviewPlayer, { VideoReviewPlayerRef } from './VideoReviewPlayer';
import { Infraction } from '../../services/apiService';

// Types
interface Answer {
  uuid: string;
  questionId: string;
  questionContent: string;
  content: string;
  recorder: string;
  frames: string;
  duration: number;
  language: string;
  metadata: {
    typingSpeed: number;
    totalKeystrokes: number;
    suspiciousEvents: string[];
    timestamp: string;
  };
}


interface SecurityFlagsProps {
  answers: Answer[];
  interviewId: string;
  infractions?: Infraction[];
  candidateName: string;
  duration: number;
  onJumpToReplay: (questionIndex: number) => void;
}


const SecurityFlags: React.FC<SecurityFlagsProps> = ({
  answers,
  interviewId,
  infractions = [],
  candidateName,
  duration,
  onJumpToReplay,
}) => {
  const videoPlayerRef = useRef<VideoReviewPlayerRef>(null);
  const [seekingTimestamp, setSeekingTimestamp] = useState<number | null>(null);

  // Handle jumping to specific timestamp in video
  const handleJumpToTimestamp = async (timestampMs: number): Promise<void> => {
    if (!videoPlayerRef.current) {
      console.warn('[SecurityFlags] Video player not available for seeking');
      return;
    }

    const interviewStartTime = videoPlayerRef.current.getInterviewStartTime();
    if (!interviewStartTime) {
      console.warn('[SecurityFlags] Interview start time not available for timestamp calculation');
      return;
    }

    try {
      // Show visual feedback
      setSeekingTimestamp(timestampMs);
      
      // Convert absolute infraction timestamp to relative video seconds
      const infractionTime = timestampMs; // Already in milliseconds
      const startTime = new Date(interviewStartTime).getTime();
      const relativeSeconds = (infractionTime - startTime) / 1000;

      if (relativeSeconds < 0) {
        console.warn('[SecurityFlags] Infraction timestamp is before interview start:', {
          infraction: new Date(timestampMs).toISOString(),
          start: interviewStartTime,
          relative: relativeSeconds
        });
        setSeekingTimestamp(null);
        return;
      }

      console.log('[SecurityFlags] Seeking to timestamp:', {
        infraction: new Date(timestampMs).toISOString(),
        start: interviewStartTime,
        relativeSeconds
      });

      const success = await videoPlayerRef.current.seekToTime(relativeSeconds);
      
      if (success) {
        // Auto-scroll video player to center of viewport
        videoPlayerRef.current.scrollIntoView();
        
        // Auto-play video after seeking (with a small delay to ensure seek completed)
        setTimeout(async () => {
          const playSuccess = await videoPlayerRef.current?.play();
          if (playSuccess) {
            console.log('[SecurityFlags] Auto-played video after jumping to timestamp');
          } else {
            console.warn('[SecurityFlags] Could not auto-play video (user interaction may be required)');
          }
        }, 100);
      } else {
        console.error('[SecurityFlags] Failed to seek video to timestamp');
      }
      
      // Clear visual feedback
      setSeekingTimestamp(null);
    } catch (error) {
      console.error('[SecurityFlags] Error converting timestamp:', error);
      setSeekingTimestamp(null);
    }
  };

  // Categorize infractions using robust categorization logic (videos excluded)
  const categorizedInfractions = React.useMemo(() => {
    const infractions_list: Infraction[] = [];
    const routineWebcam: Infraction[] = [];
    const uncategorized: Infraction[] = [];

    infractions.forEach(infraction => {
      // Debug logging for categorization
      const isInterviewEndPhoto = infraction.description?.includes('interview_end');
      const isVideoEvidence = infraction.evidence_type === 'video';
      
      if (isInterviewEndPhoto || isVideoEvidence) {
        console.log('[SECURITY_FLAGS] [DEBUG] Categorizing item:', {
          sk: infraction.sk,
          description: infraction.description,
          evidence_type: infraction.evidence_type,
          type: infraction.type,
          metadata: infraction.metadata,
          isInterviewEndPhoto,
          isVideoEvidence
        });
      }

      // INFRACTIONS: Actual violations requiring attention
      const isInfraction = () => {
        if (infraction.description?.includes('Violation:')) return true;
        if (infraction.metadata?.is_routine_monitoring === 'false' && 
            infraction.metadata?.violation_id) return true;
        if (['window_focus_lost', 'prohibited_app_detected', 'multiple_monitors_detected', 
             'display_change_detected', 'clipboard_access', 'network_activity',
             'security_event', 'window-focus', 'prohibited-app', 'multiple-monitors'].includes(infraction.type)) return true;
        if (infraction.sk?.startsWith('viol_')) return true;
        return false;
      };

      // VIDEOS: Interview recordings (skip these entirely - they belong in VideoReviewPlayer)
      const isVideo = () => {
        return infraction.evidence_type === 'video' || 
               infraction.type === 'video_recording' ||
               infraction.description?.includes('Final interview video') ||
               (infraction.type === 'screenshot_upload' && infraction.description?.includes('Final interview video')); // Backward compatibility
      };

      // ROUTINE WEBCAM MONITORING: All webcam photos for monitoring
      const isRoutineWebcam = () => {
        // Skip videos entirely - they belong in VideoReviewPlayer, not evidence sections
        if (infraction.evidence_type === 'video' || infraction.type === 'video_recording') return false;
        
        // Type indicates monitoring (but exclude video_recording)
        if (['webcam_monitoring', 'screenshot_upload'].includes(infraction.type)) return true;
        
        if (infraction.metadata?.captureMethod === 'webcam') return true;
        if (infraction.metadata?.is_routine_monitoring === 'true') return true;
        if (infraction.metadata?.captureMethod === 'general_interview_monitoring') return true;
        if (infraction.metadata?.captureReason &&
            ['interview_start', 'interview_end', 'time_interval'].includes(infraction.metadata.captureReason)) return true;
        if (infraction.description?.includes('webcam photo') || 
            infraction.description?.includes('Security event webcam') ||
            infraction.description?.includes('General interview photo')) return true;
        return false;
      };

      const infractionResult = isInfraction();
      const videoResult = isVideo();
      const routineWebcamResult = isRoutineWebcam();

      // Skip videos entirely - they belong in VideoReviewPlayer, not in evidence categorization
      if (videoResult) {
        console.log('[SECURITY_FLAGS] [DEBUG] Skipping video evidence (belongs in VideoReviewPlayer):', {
          sk: infraction.sk,
          type: infraction.type,
          evidence_type: infraction.evidence_type,
          description: infraction.description
        });
        return; // Skip videos completely
      }

      if (isInterviewEndPhoto || isVideoEvidence) {
        console.log('[SECURITY_FLAGS] [DEBUG] Categorization results:', {
          sk: infraction.sk,
          isInfraction: infractionResult,
          isVideo: videoResult,
          isRoutineWebcam: routineWebcamResult,
          finalCategory: infractionResult ? 'infractions' : routineWebcamResult ? 'routineWebcam' : 'uncategorized'
        });
      }

      if (infractionResult) {
        infractions_list.push(infraction);
      } else if (routineWebcamResult) {
        routineWebcam.push(infraction);
      } else {
        uncategorized.push(infraction);
        console.warn('[SECURITY_FLAGS] Uncategorized infraction:', {
          sk: infraction.sk,
          description: infraction.description,
          type: infraction.type,
          metadata: infraction.metadata
        });
      }
    });

    return { infractions: infractions_list, routineWebcam, uncategorized };
  }, [infractions]);

  // Updated statistics using new categorization (videos excluded - handled by VideoReviewPlayer)
  const stats = {
    actualViolations: categorizedInfractions.infractions.length,
    evidenceFiles: infractions.length,
    routineMonitoring: categorizedInfractions.routineWebcam.length,
    uncategorized: categorizedInfractions.uncategorized.length,
  };

  // Debug logging for stats
  console.log('[SECURITY_FLAGS] [DEBUG] Final stats:', stats);
  console.log('[SECURITY_FLAGS] [DEBUG] Routine webcam items:', categorizedInfractions.routineWebcam.map(item => ({
    sk: item.sk,
    description: item.description,
    evidence_type: item.evidence_type
  })));
  // Videos are now handled by VideoReviewPlayer, not categorized here


  // Convert technical descriptions to human-readable format (same as Infraction component)
  const getHumanReadableDescription = (description: string) => {
    const eventPatterns: Record<string, string> = {
      'Security event: window_focus_lost': 'Window Focus Lost',
      'Security event: window_blur': 'Window Minimized/Hidden',
      'Security event: multiple_monitors_detected': 'Multiple Monitors Detected',
      'Security event: display_change': 'Display Configuration Changed',
      'Security event: prohibited_app_detected': 'Prohibited Application Detected',
      'Security event: clipboard_copy': 'Clipboard Copy Activity',
      'Security event: clipboard_paste': 'Clipboard Paste Activity',
      'Security event: system_suspicious': 'Suspicious System Activity',
      'Violation: window_focus_lost - Application lost focus': 'Application Lost Focus',
      'Violation: window_blur - Window minimized': 'Window Minimized',
      'Violation: multiple_monitors_detected - Multiple displays': 'Multiple Displays Detected',
      'Violation: prohibited_app_detected - Unauthorized software': 'Unauthorized Software Detected',
    };

    if (eventPatterns[description]) {
      return eventPatterns[description];
    }

    if (description.startsWith('Security event: ')) {
      const eventType = description.replace('Security event: ', '');
      return eventType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }

    if (description.startsWith('Violation: ') && description.includes(' - ')) {
      const parts = description.split(' - ');
      if (parts.length >= 2) {
        return parts[1];
      }
    }

    if (description.includes('Question ') && description.includes('screenshot')) {
      const match = description.match(/Question (\d+)/);
      return match ? `Question ${match[1]} Screenshot` : 'Question Screenshot';
    }

    // Handle interview end photos FIRST (specific match)
    if (description.includes('interview_end')) {
      return 'Interview End Photo';
    }

    // Handle interview start photos (general match)
    if (description.includes('General interview photo') || description.includes('interview_start')) {
      return 'Interview Start Photo';
    }

    return description.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Export security report
  const exportSecurityReport = () => {
    const report = {
      interview: {
        totalInfractions: infractions.length,
        securityEvents: stats.total,
        questionsMonitored: stats.questionsMonitored,
        timestamp: new Date().toISOString(),
      },
      summary: stats,
      infractions: infractions,
    };
    
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `security-report-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (infractions.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="success" sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CheckCircle />
            <Box>
              <Typography variant="h6">Clean Interview</Typography>
              <Typography variant="body2">
                No security flags detected. The candidate's behavior appears normal throughout the interview.
              </Typography>
            </Box>
          </Box>
        </Alert>
        
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                <Typography variant="h4" color="success.main">
                  0
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Questions Monitored
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Security sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                <Typography variant="h4" color="primary.main">
                  0
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Security Flags
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Schedule sx={{ fontSize: 48, color: 'info.main', mb: 1 }} />
                <Typography variant="h4" color="info.main">
                  0
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Evidence Files
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Video Review Section */}
      <Box sx={{ mb: 3 }}>
        <VideoReviewPlayer
          ref={videoPlayerRef}
          interviewId={interviewId}
          candidateName={candidateName}
          duration={duration}
        />
      </Box>

      {/* Infractions Section */}
      {stats.actualViolations > 0 && (
        <Paper elevation={1} sx={{ mb: 3 }}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
              <Warning color="error" />
              Infractions ({stats.actualViolations})
            </Typography>
            
            <InfractionComponent
              interviewId={interviewId}
              infractions={categorizedInfractions.infractions}
              onJumpToReplay={(timestamp) => {
                handleJumpToTimestamp(timestamp);
              }}
              seekingTimestamp={seekingTimestamp}
            />
          </Box>
        </Paper>
      )}


      {/* Routine Monitoring Section */}
      {stats.routineMonitoring > 0 && (
        <Paper elevation={1}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
              <Videocam color="primary" />
              Routine Monitoring ({stats.routineMonitoring} files)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Standard interview monitoring screenshots captured during the session.
            </Typography>
            
            <InfractionComponent
              interviewId={interviewId}
              infractions={categorizedInfractions.routineWebcam}
              isRoutineMonitoring={true}
              onJumpToReplay={(timestamp) => {
                handleJumpToTimestamp(timestamp);
              }}
              seekingTimestamp={seekingTimestamp}
            />
          </Box>
        </Paper>
      )}

      {/* Uncategorized Items (Debug/Error Handling) */}
      {stats.uncategorized > 0 && (
        <Paper elevation={1} sx={{ border: '1px solid orange', backgroundColor: 'rgba(255, 152, 0, 0.05)' }}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'warning.main' }}>
              <Warning color="warning" />
              Uncategorized Items ({stats.uncategorized})
            </Typography>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Some evidence items could not be automatically categorized. This may indicate:
              </Typography>
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                <li>New data format not yet supported</li>
                <li>Missing or malformed metadata</li>
                <li>Backend categorization logic needs updating</li>
              </ul>
              <Typography variant="body2">
                These items are displayed below for manual review.
              </Typography>
            </Alert>
            
            <InfractionComponent
              interviewId={interviewId}
              infractions={categorizedInfractions.uncategorized}
              onJumpToReplay={(timestamp) => {
                handleJumpToTimestamp(timestamp);
              }}
              seekingTimestamp={seekingTimestamp}
            />
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default SecurityFlags;